import {
  PopupMenuHandler,
  type Cell,
  type MaxPopupMenu,
} from '@maxgraph/core';
import type { WorkflowGraph } from '@/graph/WorkflowGraph';
import { classify } from '@/graph/domain/classify';
import { useGraphEditorStore } from '@/graph-store';
import { buildEmptyCanvasMenu } from './buildEmptyCanvasMenu';
import { buildMaterialMenu } from './buildMaterialMenu';
import { buildOperationMenu } from './buildOperationMenu';
import { buildGroupMenu } from './buildGroupMenu';
import { buildEdgeMenu } from './buildEdgeMenu';

export interface ToastCallbacks {
  showWarning: (message: string) => void;
  showSuccess: (message: string) => void;
}

export class ContextMenuService {
  private popupHandler: PopupMenuHandler | null = null;
  private removeOutsideClickListener: (() => void) | null = null;

  constructor(private graph: WorkflowGraph, private toast?: ToastCallbacks) {
    const handler = graph.getPlugin<PopupMenuHandler>('PopupMenuHandler');
    if (handler) {
      this.popupHandler = handler;
      handler.factoryMethod = (menu, cell, event) =>
        this.build(menu as MaxPopupMenu, cell, event as MouseEvent);
      this.bindOutsideClickToClose(handler);
    }
  }

  destroy(): void {
    if (this.removeOutsideClickListener) {
      this.removeOutsideClickListener();
      this.removeOutsideClickListener = null;
    }
    this.popupHandler = null;
  }

  private bindOutsideClickToClose(handler: PopupMenuHandler): void {
    const doc = this.graph.container?.ownerDocument ?? document;

    const getMenuElement = (): HTMLElement | null => {
      const unsafeHandler = handler as unknown as {
        div?: HTMLElement | null;
        popupMenu?: { div?: HTMLElement | null };
        menu?: { div?: HTMLElement | null };
      };
      return unsafeHandler.div ?? unsafeHandler.popupMenu?.div ?? unsafeHandler.menu?.div ?? null;
    };

    const isMenuShowing = (): boolean => {
      const unsafeHandler = handler as unknown as { isMenuShowing?: () => boolean };
      if (typeof unsafeHandler.isMenuShowing === 'function') {
        return unsafeHandler.isMenuShowing();
      }
      const menuEl = getMenuElement();
      return Boolean(menuEl && menuEl.style.display !== 'none');
    };

    const hideMenu = () => {
      const unsafeHandler = handler as unknown as { hideMenu?: () => void };
      unsafeHandler.hideMenu?.();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!isMenuShowing()) return;

      const menuEl = getMenuElement();
      const target = event.target as Node | null;
      if (!menuEl || !target) {
        hideMenu();
        return;
      }

      if (!menuEl.contains(target)) {
        hideMenu();
      }
    };

    doc.addEventListener('pointerdown', onPointerDown, true);
    this.removeOutsideClickListener = () => {
      doc.removeEventListener('pointerdown', onPointerDown, true);
    };
  }

  private build(menu: MaxPopupMenu, cell: Cell | null, event: MouseEvent): void {
    const store = useGraphEditorStore.getState();
    this.applyMenuVariantClass(menu, null);

    if (!cell) {
      const point = this.eventToGraphCoords(event);
      buildEmptyCanvasMenu(menu, point, store, this.toast);
      return;
    }

    const kind = classify(cell);
    switch (kind) {
      case 'material':
        this.applyMenuVariantClass(menu, 'material');
        buildMaterialMenu(menu, cell.getId()!, store, this.toast);
        break;
      case 'operation':
        buildOperationMenu(menu, cell.getId()!, store, this.toast);
        break;
      case 'group':
        buildGroupMenu(menu, cell.getId()!, store, this.toast);
        break;
      case 'flowEdge':
        buildEdgeMenu(menu, cell, store, this.toast);
        break;
    }
  }

  private applyMenuVariantClass(menu: MaxPopupMenu, variant: 'material' | null): void {
    const menuWithDiv = menu as unknown as { div?: HTMLElement };
    const menuElement = menuWithDiv.div;
    if (!menuElement) {
      return;
    }

    menuElement.classList.remove('material-context-menu');
    if (variant === 'material') {
      menuElement.classList.add('material-context-menu');
    }
  }

  private eventToGraphCoords(event: MouseEvent): { x: number; y: number } {
    const view = this.graph.getView();
    const container = this.graph.container as HTMLElement;
    const rect = container.getBoundingClientRect();
    const x = (event.clientX - rect.left) / view.scale - view.translate.x;
    const y = (event.clientY - rect.top) / view.scale - view.translate.y;
    return { x, y };
  }
}
